import { defineStore } from 'pinia';
import { loadStripe } from '@stripe/stripe-js';
import requests from '../../../_00_elements/tools/requests';
import {
  URL_XHR_OFFERS,
  URL_XHR_GET_UNIQUE_BUYERS,
  URL_XHR_SUBSCRIPTION_CREATE,
  URL_XHR_SUBSCRIPTION_MODIFY,
  URL_XHR_GET_ONBOARDING_LINK,
  URL_XHR_DISCOUNT_ENDPOINT,
  PAY_SUBSCRIPTION_ENDPOINT,
} from '../../../_00_elements/tools/routes/routes';
import { getHumanDate } from '../../../_00_elements/tools/date';
import OfficeStateEnum from '../../offer/office-state-enum';
import eventBus from '../../../_00_elements/tools/event-bus';
import { flashBagError, flashBagSuccess } from '../../../_00_elements/tools/flashbag';
import { trans } from '../../../_00_elements/translation';
import useManagePayAccount from './managePayAccount';
import useAccountStore from './account';

export const SUBSCRIPTION_TITLE_MAX_LENGTH = 255;

export default defineStore('subscription', {
  state: () => ({
    payOrganizationBuyer: {
      phone: null,
      companyName: null,
      siren: null,
      vatNumber: null,
      address: null,
      email: null,
    },
    clientInvoiceData: {
      legalName: null,
      siren: null,
      vatNumber: null,
      phoneNumber: null,
      streetAndNumber: null,
      additionalInfo: null,
      zipCode: null,
      city: null,
      email: null,
    },
    invoice: {
      deal: null,
      invoiceTitle: null,
      amount: null,
      startDate: null,
      endDate: null,
      vatRate: 2000,
      taxAmount: null,
      totalAmount: null,
    },
    selectedValuesList: {
      customer: null,
      offices: null,
    },
    offices: [],
    signedDeals: [],
    currentPage: 1,
    hasMoreResults: true,
    officesAvailable: [],
    subscribersData: [],
    selectedSubscriptionData: null,
    onboardingClientSecret: null,
    isNotUbiqCustomer: true,
    preselectedDeal: null,
    isLoading: false,
    errors: {},
  }),
  actions: {
    async createAccountToken() {
      const stripe = await loadStripe(window.STRIPE_API_KEY);
      if (!stripe) {
        throw new Error('Stripe.js failed to initialize');
      }
      const accountStore = useAccountStore();
      const { token, error } = await stripe.createToken('account', {
        business_type: 'company',
        company: {
          name: accountStore.companyName ?? window.$companyName,
        },
        tos_shown_and_accepted: true,
      });
      if (error) {
        throw error;
      }
      return token.id;
    },
    resetFunnel() {
      this.payOrganizationBuyer = {
        phone: null,
        companyName: null,
        siren: null,
        vatNumber: null,
        address: null,
        email: null,
      };
      this.invoice = {
        deal: null,
        invoiceTitle: null,
        amount: null,
        startDate: null,
        endDate: null,
      };
      this.selectedValuesList = {
        customer: null,
        offices: null,
      };
    },
    resetSignedDeals() {
      this.signedDeals = [];
    },
    setSelectedValuesList(category, value) {
      this.selectedValuesList[category] = value;
    },
    setIsNotUbiqCustomer(value) {
      this.isNotUbiqCustomer = value;
    },
    setPreselectedDeal(value) {
      this.preselectedDeal = value;
    },
    setSubscribersData(subscribers) {
      this.subscribersData = subscribers.map((subscriber) => {
        const createdAt = subscriber.created_at ? getHumanDate(subscriber.created_at) : null;
        const endSubscription = subscriber.end_subscription ? getHumanDate(subscriber.end_subscription) : null;

        return {
          ...subscriber,
          period: endSubscription ? `${createdAt} - ${endSubscription}` : `${trans('global.since')} ${createdAt}`,
        };
      });
    },
    setData(property, data) {
      this[property] = data;
    },
    setAmount(data) {
      this.invoice.amount = data;
    },
    setInvoiceTitle(data) {
      this.invoice.invoiceTitle = data?.length > SUBSCRIPTION_TITLE_MAX_LENGTH
        ? `${data.substring(0, SUBSCRIPTION_TITLE_MAX_LENGTH - 3)}...`
        : data;
    },
    setBuyerData(data) {
      this.payOrganizationBuyer.companyName = data.companyName;
      this.payOrganizationBuyer.organization = data.companyId;
      this.invoice.deal = data.id;
      this.payOrganizationBuyer.email = data.email;
      this.payOrganizationBuyer.phone = data.phone_number;
      this.clientInvoiceData.legalName = data.buyer_legal_name;
      this.clientInvoiceData.streetAndNumber = data.buyer_street_and_number;
      this.clientInvoiceData.additionalInfo = data.buyer_additional_info;
      this.clientInvoiceData.zipCode = data.buyer_zip_code;
      this.clientInvoiceData.city = data.buyer_city;
      this.clientInvoiceData.siren = data.buyer_siren;
      this.clientInvoiceData.vatNumber = data.buyer_vat_number;
      this.clientInvoiceData.email = data.buyer_email;
      this.clientInvoiceData.phoneNumber = data.buyer_phone_number;
      this.clientInvoiceData.payOrganizationBuyerId = data.buyer_pay_organization_buyer_id;
    },
    setSubscription(data) {
      this.invoice.startDate = data.startDate;
      this.invoice.endDate = data.endDate;
    },
    resetOfficeData() {
      this.offices = [];
    },
    addOfficeData(data) {
      this.offices.push({
        id: data.id,
        name: `${data.name} - ${data.address.name}`,
      });
    },
    setNullFirstStep() {
      this.payOrganizationBuyer = {
        phone: null,
        companyName: null,
        organization: null,
        siren: null,
        vatNumber: null,
        address: null,
        email: null,
      };
      this.clientInvoiceData = {
        legalName: null,
        siren: null,
        vatNumber: null,
        phoneNumber: null,
        streetAndNumber: null,
        additionalInfo: null,
        zipCode: null,
        city: null,
        email: null,
        payOrganizationBuyerId: null,
      };
      this.invoice.invoiceTitle = null;
      this.invoice.deal = null;
    },
    setSelectedData(data) {
      this.selectedSubscriptionData = data ? { ...data } : null;
    },
    async createDiscount(title, price) {
      const discount = {
        subscription: this.selectedSubscriptionData.id,
        title,
        price,
      };

      try {
        this.loading = true;
        const { data } = await requests.post(URL_XHR_DISCOUNT_ENDPOINT, discount);
        flashBagSuccess(trans('gestion.invoices.slider.credit_note.flashbag.success'));

        return data;
      } catch (error) {
        flashBagError(error.response?.data?.message);
      } finally {
        this.loading = false;
      }

      return null;
    },
    isLoading() {
      return this.loading === true;
    },
    async createSubscription() {
      const payOrganizationBuyer = {
        ...this.payOrganizationBuyer,
        clientInvoiceData: this.clientInvoiceData,
      };

      const subscription = {
        ...this.invoice,
        payOrganizationBuyer,
        title: this.invoice.invoiceTitle,
      };

      if (this.invoice.deal) {
        subscription.deal = `/api/deals/${this.invoice.deal}`;
      }

      if (this.payOrganizationBuyer.organization) {
        subscription.payOrganizationBuyer.organization = `/api/organizations/${this.payOrganizationBuyer.organization}`;
      }

      const subscriptionData = {
        payOrganizationBuyer,
        subscription,
        offices: this.offices,
      };

      if (this.clientInvoiceData.payOrganizationBuyerId) {
        subscriptionData.payOrganizationBuyerId = this.clientInvoiceData.payOrganizationBuyerId;
      }

      try {
        const response = await requests.post(URL_XHR_SUBSCRIPTION_CREATE, subscriptionData);
        if (response.status === 200 || response.status === 201) {
          window.initialState.subscriptions = response.data.reload.subscriptions;
          this.setSubscribersData(response.data.reload.subscriptions);
          flashBagSuccess(trans('gestion.pay.subscription.creation.flashbag.success'));
          this.resetFunnel();
        } else {
          flashBagError(response.message);
        }
      } catch (error) {
        flashBagError(error.response.data.message);
      }
    },
    async updateSubscription(data) {
      const response = await requests.post(URL_XHR_SUBSCRIPTION_MODIFY.replace('{id}', data.id), {
        amount: data.amount,
        endDate: data.endDate,
        subscriptionId: data.subscriptionId,
      });
      if (response.status === 200 || response.status === 201) {
        flashBagSuccess(trans('gestion.pay.subscription.modification.flashbag.success'));
      }
      this.setSelectedData(response.data.reload);
      return response;
    },
    async fetchSignedDeals(mode = 'short', search = '') {
      const queryParams = new URLSearchParams();
      if (this.preselectedDeal) {
        queryParams.append('preselectedDeal', this.preselectedDeal);
      }

      if (this.selectedValuesList.customer) {
        queryParams.append('preselectedDeal', this.selectedValuesList.customer);
      }

      if (search) {
        queryParams.append('search', search);
      }
      queryParams.append('mode', mode);
      const queryString = queryParams.toString() !== '' ? `?${queryParams.toString()}` : '';

      const { data } = await requests.get(`${URL_XHR_GET_UNIQUE_BUYERS}${queryString}`);
      this.signedDeals = data.users;
      this.hasMoreResults = this.signedDeals.length < data.count;
      return null;
    },
    async fetchOffices(params = {}) {
      const queryParams = new URLSearchParams();
      queryParams.append('states', [
        OfficeStateEnum.PUBLISHED,
        OfficeStateEnum.UNPUBLISHED,
        OfficeStateEnum.OFFMARKET ]);
      if (params.search) {
        queryParams.append('search', params.search);
      }
      const queryString = queryParams.toString() !== '' ? `${queryParams.toString()}` : '';
      const response = await requests.get(`${URL_XHR_OFFERS}?${queryString}`);
      this.officesAvailable = response.data.offers;
    },
    async getSubscription(id) {
      const response = await requests.get(`${PAY_SUBSCRIPTION_ENDPOINT}/${id}`);
      this.setSelectedData(response.data);
    },
    async getOnboardingClientSecret(accountToken = '') {
      const managePayAccount = useManagePayAccount();
      const response = await requests.get(URL_XHR_GET_ONBOARDING_LINK, {
        params: {
          account_token: accountToken,
        },
      });
      if (!managePayAccount.payOrganizationBillingInformation) {
        window.initialState.payOrganizationSeller = {
          id: response.data.reload.pay_organization_seller_id,
        };
      }
      this.onboardingClientSecret = response.data.reload.client_secret;
    },
    addError(field, message) {
      this.errors[field] = message;
    },
    getError(field) {
      return this.errors[field];
    },
    hasError(field) {
      return !!this.errors[field];
    },
    clearError(field) {
      delete this.errors[field];
    },
  },
});
